import React, { lazy } from 'react';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { GoogleReviews } from '../components/GoogleReviews';
import { CallbackForm } from '../components/CallbackForm';
import { MapPin, MessageCircle, Mail, Calendar, Instagram, Phone } from 'lucide-react';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
const INSTAGRAM_URL = 'https://instagram.com/dentico_clinic';
const MAPS_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1241.0181846982155!2d-0.104187760804084!3d51.53089276153758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760f7c6534e077%3A0xa54a590bd3b2a149!2sDentico%20Clinic%20-%20Private%20Dentist%20in%20Angel%2C%20Islington!5e0!3m2!1suk!2suk!4v1769722166469!5m2!1suk!2suk';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-1508">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-1509" />
    </svg>;
}
export function ContactPage() {
  const {
    t,
    lang
  } = useTranslation();
  const seoTitle = lang === 'en' ? 'Contact Dentico Clinic | Private Dentist Angel London' : lang === 'ua' ? 'Контакти Dentico Clinic | Приватний стоматолог Angel' : lang === 'ro' ? 'Contact Dentico Clinic | Dentist Privat Angel Londra' : 'Контакты Dentico Clinic | Частный стоматолог Angel';
  const seoDescription = lang === 'en' ? 'Contact Dentico Clinic in Angel, London. Book online, call, WhatsApp or visit our private dental clinic on City Road, EC1V.' : lang === 'ua' ? "Зв'яжіться з Dentico Clinic в Angel, Лондон. Записуйтесь онлайн, телефонуйте, пишіть у WhatsApp або відвідайте нашу клініку на City Road, EC1V." : lang === 'ro' ? 'Contactați Dentico Clinic din Angel, Londra. Programați online, sunați, WhatsApp sau vizitați clinica noastră privată de pe City Road, EC1V.' : 'Свяжитесь с Dentico Clinic в Angel, Лондон. Записывайтесь онлайн, звоните, пишите в WhatsApp или посетите нашу клинику на City Road, EC1V.';
  return <Layout title={seoTitle} description={seoDescription} data-id="element-1510">
      <div className="bg-forest-900 py-20 text-white" data-id="element-1511">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1512">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1513">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-ivory/80 max-w-2xl mx-auto" data-id="element-1514">
            {t('contact.subtitle')}
          </p>
          <p className="text-base text-ivory/60 max-w-2xl mx-auto mt-4" data-id="element-1515">
            {lang === 'en' ? 'You can find Dentico Clinic at 360–364 City Road, London EC1V 2PY, close to Angel Station.' : lang === 'ua' ? 'Ви можете знайти Dentico Clinic за адресою 360–364 City Road, London EC1V 2PY, поруч зі станцією Angel.' : lang === 'ro' ? 'Puteți găsi Dentico Clinic la adresa 360–364 City Road, London EC1V 2PY, aproape de stația Angel.' : 'Вы можете найти Dentico Clinic по адресу 360–364 City Road, London EC1V 2PY, рядом со станцией Angel.'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-1516">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-1517">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-8" data-id="element-1518">
            <Card className="p-8" data-id="element-1519">
              <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-1520">
                {t('contact.getInTouch')}
              </h2>

              <div className="space-y-6" data-id="element-1521">
                {/* Address */}
                <div className="flex items-start gap-4" data-id="element-1522">
                  <div className="p-2 bg-ivory-200 rounded-lg text-forest-600" data-id="element-1523">
                    <MapPin className="w-6 h-6" data-id="element-1524" />
                  </div>
                  <div data-id="element-1525">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-1526">
                      {t('contact.address')}
                    </h3>
                    <p className="text-charcoal-light" data-id="element-1527">
                      360–364 City Rd
                      <br data-id="element-1528" />
                      London EC1V 2PY
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4" data-id="element-1529">
                  <div className="p-2 bg-ivory-200 rounded-lg text-forest-600" data-id="element-1530">
                    <Phone className="w-6 h-6" data-id="element-1531" />
                  </div>
                  <div data-id="element-1532">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-1533">
                      {lang === 'en' ? 'Phone' : lang === 'ua' ? 'Телефон' : lang === 'ro' ? 'Telefon' : 'Телефон'}
                    </h3>
                    <a href="tel:+447345197820" className="text-forest-600 hover:underline font-medium" data-id="element-1534">
                      07345 197820
                    </a>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-start gap-4" data-id="element-1535">
                  <div className="p-2 bg-ivory-200 rounded-lg text-forest-600" data-id="element-1536">
                    <MessageCircle className="w-6 h-6" data-id="element-1537" />
                  </div>
                  <div data-id="element-1538">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-1539">
                      {t('contact.whatsapp')}
                    </h3>
                    <p className="text-charcoal-light mb-2" data-id="element-1540">
                      {t('contact.whatsappDesc')}
                    </p>
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-1541">
                      <Button size="sm" className="gap-2 bg-forest-600 text-white border border-forest-600 hover:bg-ivory hover:text-forest-600" data-id="element-1542">
                        <MessageCircle className="w-4 h-4" data-id="element-1543" />
                        {t('contact.chatOnWhatsapp')}
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Telegram */}
                <div className="flex items-start gap-4" data-id="element-1544">
                  <div className="p-2 bg-ivory-200 rounded-lg text-forest-600" data-id="element-1545">
                    <TelegramIcon className="w-6 h-6" data-id="element-1546" />
                  </div>
                  <div data-id="element-1547">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-1548">
                      Telegram
                    </h3>
                    <p className="text-charcoal-light mb-2" data-id="element-1549">
                      {lang === 'en' ? 'Message us on Telegram' : lang === 'ua' ? 'Напишіть нам у Telegram' : lang === 'ro' ? 'Scrieți-ne pe Telegram' : 'Напишите нам в Telegram'}
                    </p>
                    <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" data-id="element-1550">
                      <Button variant="outline" size="sm" className="gap-2" data-id="element-1551">
                        <TelegramIcon className="w-4 h-4" data-id="element-1552" />
                        Telegram
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4" data-id="element-1553">
                  <div className="p-2 bg-ivory-200 rounded-lg text-forest-600" data-id="element-1554">
                    <Mail className="w-6 h-6" data-id="element-1555" />
                  </div>
                  <div data-id="element-1556">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-1557">
                      {t('contact.email')}
                    </h3>
                    <a href="mailto:info@denticoclinic.co.uk" className="text-forest-600 hover:underline break-all" data-id="element-1558">
                      info@denticoclinic.co.uk
                    </a>
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex items-start gap-4" data-id="element-1559">
                  <div className="p-2 bg-ivory-200 rounded-lg text-forest-600" data-id="element-1560">
                    <Instagram className="w-6 h-6" data-id="element-1561" />
                  </div>
                  <div data-id="element-1562">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-1563">
                      {t('contact.instagram')}
                    </h3>
                    <p className="text-charcoal-light mb-2" data-id="element-1564">
                      {t('contact.instagramDesc')}
                    </p>
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-forest-600 font-medium hover:underline" data-id="element-1565">
                      @dentico_clinic
                    </a>
                  </div>
                </div>
              </div>

              {/* Book Online Button */}
              <div className="mt-8 pt-6 border-t border-beige/60" data-id="element-1566">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-1567">
                  <Button fullWidth size="lg" className="gap-2" data-id="element-1568">
                    <Calendar className="w-5 h-5" data-id="element-1569" />
                    {t('nav.bookOnline')}
                  </Button>
                </a>
              </div>
            </Card>

            <Card className="p-8" data-id="element-1570">
              <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-1571">
                {t('contact.openingHours')}
              </h2>
              <div className="space-y-3" data-id="element-1572">
                <div className="flex justify-between items-center pb-3 border-b border-beige/60" data-id="element-1573">
                  <span className="text-charcoal-light" data-id="element-1574">
                    {t('footer.monFri')}
                  </span>
                  <span className="font-medium text-charcoal" data-id="element-1575">
                    10:00 - 18:00
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-beige/60" data-id="element-1576">
                  <span className="text-charcoal-light" data-id="element-1577">
                    {t('footer.saturday')}
                  </span>
                  <span className="font-medium text-charcoal" data-id="element-1578">
                    10:00 - 18:00
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1579">
                  <span className="text-charcoal-light" data-id="element-1580">
                    {t('footer.sunday')}
                  </span>
                  <span className="font-medium text-red-500" data-id="element-1581">
                    {t('footer.closed')}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {/* Map */}
          <div className="lg:col-span-2" data-id="element-1582">
            <div className="bg-stone-200 rounded-2xl overflow-hidden h-full min-h-[500px] shadow-lg" data-id="element-1583">
              <iframe src={MAPS_EMBED_URL} width="100%" height="100%" style={{
              border: 0,
              minHeight: '500px'
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Dentico Clinic Location" data-id="element-1584"></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Callback Form Section */}
      <section className="py-16 bg-ivory-200" data-id="element-1585">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1586">
          <CallbackForm data-id="element-1587" />
        </div>
      </section>

      <GoogleReviews data-id="element-1588" />
    </Layout>;
}